import type { EntryFormAction, FieldInputAction, SuggestedAction } from '@vivport/backend/types';
import { Button } from '@/components/ui/button';
import { EntryFormActionCard, type EntryFormSubmit } from './EntryFormActionCard';
import { FieldInputActionCard } from './FieldInputActionCard';

interface SuggestedActionsBarProps {
  actions: SuggestedAction[];
  disabled: boolean;
  conversationPhotoIds: number[];
  onActionClick: (action: SuggestedAction) => void;
  onFieldInputSubmit: (action: FieldInputAction, value: string) => Promise<void>;
  onEntryFormSubmit: (action: EntryFormAction, submit: EntryFormSubmit) => Promise<void>;
}

function isFieldInput(action: SuggestedAction): action is FieldInputAction {
  return action.kind === 'FIELD_INPUT';
}

function isEntryForm(action: SuggestedAction): action is EntryFormAction {
  return action.kind === 'ENTRY_FORM';
}

export function SuggestedActionsBar({
  actions,
  disabled,
  conversationPhotoIds,
  onActionClick,
  onFieldInputSubmit,
  onEntryFormSubmit,
}: SuggestedActionsBarProps) {
  if (actions.length === 0) {
    return null;
  }
  const fieldInputs = actions.filter(isFieldInput);
  const entryForms = actions.filter(isEntryForm);
  const chips = actions.filter((action) => !isFieldInput(action) && !isEntryForm(action));

  return (
    <div className="px-4 py-2 border-t border-border bg-muted/20 space-y-2">
      {fieldInputs.length > 0 || entryForms.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Details</p>
          {fieldInputs.map((action) => (
            <FieldInputActionCard
              key={`${action.payload.sectionCode}:${action.payload.fieldKey}`}
              action={action}
              disabled={disabled}
              onSubmit={(value) => onFieldInputSubmit(action, value)}
            />
          ))}
          {entryForms.map((action) => (
            <EntryFormActionCard
              key={`entry:${action.payload.sectionCode}:${action.payload.prefill?.entryNumber ?? 'new'}`}
              action={action}
              disabled={disabled}
              conversationPhotoIds={conversationPhotoIds}
              onSubmit={(submit) => onEntryFormSubmit(action, submit)}
            />
          ))}
        </div>
      ) : null}
      {chips.length > 0 ? (
        <>
          <p className="text-xs text-muted-foreground">Suggested next step</p>
          <div className="flex flex-wrap gap-2">
            {chips.map((action, index) => (
              <Button
                key={`${action.kind}:${action.label}:${index}`}
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => onActionClick(action)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
